import moment from 'moment';
import {
    AVERAGE_UNAVAILABLE,
    WEEKLY_THRESHOLD_DAYS,
    daysInPeriod,
} from '../averages';
import { NO_CAMPAIGN_WINDOW, oldestComputedAt } from './create-campaign-source';
import type { CampaignSourceConfig } from './create-campaign-source';
import type {
    KpiCardDataRequest,
    KpiCardState,
    KpiMetric,
    KpiRateWindow,
    KpiSource,
    KpiSourceDeps,
    ResolvedPeriod,
} from '../types';
import type { EnvelopeCaveat } from '~stores/kpi-envelope';
import { analyticsToday, resolvePeriodRequest } from '../periods';
import type { PeriodRequest } from '../periods';

const DATE_FORMAT = 'YYYY-MM-DD';

const WINDOW_FAILED = 'That window could not be read.';

/** A window that has not answered yet is not a window that failed: the card
 * waits as a whole rather than settling on a degraded trio. */
const hasFailed = (state: KpiCardState): boolean =>
    state.status === 'unavailable' || state.status === 'error';

const failureReason = (state: KpiCardState): string =>
    state.status === 'unavailable' ? state.reason : WINDOW_FAILED;

type WindowRate =
    | {
          served: true;
          window: KpiRateWindow;
          caveats?: EnvelopeCaveat[];
          computedAt?: string;
      }
    | { served: false; state: KpiCardState };

/** A window's real rate, read through the envelope source's own `rate` path
 * (never a total divided down from another window, see this module's header
 * comment). */
const windowRate = async (
    base: KpiSource,
    req: KpiCardDataRequest,
    period: ResolvedPeriod,
    deps: KpiSourceDeps
): Promise<WindowRate> => {
    const state = await base.getCardData(
        { ...req, form: 'rate', period, compare: undefined },
        deps
    );
    if (state.status !== 'ready') {
        return { served: false, state };
    }
    if (state.data.form !== 'rate') {
        return { served: false, state: { status: 'error' } };
    }
    return {
        served: true,
        window: {
            numerator: state.data.numerator,
            denominator: state.data.denominator,
            rate: state.data.rate,
        },
        ...(state.caveats && { caveats: state.caveats }),
        ...(state.computedAt !== undefined && {
            computedAt: state.computedAt,
        }),
    };
};

/** The part of `window` the campaign actually ran for, or `null` when the two
 * do not overlap. The selected period is free to run past either end of the
 * campaign, and a derived window landing outside it reads an empty rate that
 * is a fact about the calendar, not about the campaign. */
const clampToCampaign = (
    window: ResolvedPeriod,
    campaign: ResolvedPeriod
): ResolvedPeriod | null => {
    const start = window.start > campaign.start ? window.start : campaign.start;
    const end = window.end < campaign.end ? window.end : campaign.end;
    return start > end ? null : { start, end };
};

/** The to-date window stops at yesterday, the last settled day; a campaign
 * that has not ended runs today too. */
const campaignThroughToday = (
    toDate: ResolvedPeriod,
    runsToday: boolean | undefined
): ResolvedPeriod =>
    runsToday ? { start: toDate.start, end: analyticsToday() } : toDate;

/** `period`'s own last day, as a one-day window. */
const lastDayOf = (period: ResolvedPeriod): ResolvedPeriod => ({
    start: period.end,
    end: period.end,
});

/** The last `WEEKLY_THRESHOLD_DAYS` days of `period`, ending on `period`'s own
 * end date. Distinct from `period` itself unless `period` happens to be
 * exactly that long. */
const lastWeekOf = (period: ResolvedPeriod): ResolvedPeriod => ({
    start: moment(period.end, DATE_FORMAT)
        .subtract(WEEKLY_THRESHOLD_DAYS - 1, 'days')
        .format(DATE_FORMAT),
    end: period.end,
});

const DERIVED_PRESETS: PeriodRequest[] = [
    { period: 'today' },
    { period: '7d' },
];

/** A derived window that is exactly a preset's window goes as that preset, so
 * it reads the preset's cache key instead of a dated one. */
const asPreset = (window: ResolvedPeriod | null): ResolvedPeriod | null => {
    if (!window) return null;
    const today = analyticsToday();
    for (const request of DERIVED_PRESETS) {
        const preset = resolvePeriodRequest(request, today);
        if (preset?.start === window.start && preset.end === window.end) {
            return preset;
        }
    }
    return window;
};

/**
 * Wraps a source so it also serves `rate-trio`: three REAL rate reads (the
 * campaign-to-date window, the last week of the selected period, its last
 * day), never one figure divided down from another. This is `trio`'s sibling,
 * not a replacement for it: a rate over seven days is not a total divided by
 * seven, so it needs a window of its own rather than an average derived from
 * a single selected-period read. Every other form passes straight through to
 * `base`, so a source composing this alongside `withCampaignWindows` keeps
 * that wrapper's `trio` cards unchanged.
 */
export const withCampaignRateWindows = <M extends KpiMetric>(
    base: KpiSource<M>,
    config: CampaignSourceConfig<M>
): KpiSource<M> => {
    // Widened once here (readonly arrays are covariant), same as the trio
    // wrapper does: `req.metric` below is the broad `KpiMetric`.
    const percentMetrics: readonly KpiMetric[] | undefined =
        config.percentMetrics;
    const currencyMetrics = new Set(
        config.metrics
            .filter((metric) => metric.isCurrency)
            .map((metric) => metric.id as KpiMetric)
    );

    return {
        descriptor: base.descriptor,

        async getCardData(
            req: KpiCardDataRequest,
            deps: KpiSourceDeps
        ): Promise<KpiCardState> {
            if (req.form !== 'rate-trio') {
                return base.getCardData(req, deps);
            }
            if (!req.campaignToDate) {
                return { status: 'unavailable', reason: NO_CAMPAIGN_WINDOW };
            }

            // Both derived windows are cut to the campaign. A period running
            // past either end otherwise asks for days the campaign did not
            // run, which comes back as an empty rate indistinguishable from a
            // real zero; no overlap at all is not a window to read.
            const campaign = campaignThroughToday(
                req.campaignToDate,
                req.campaignRunsToday
            );
            const dailyWindow = asPreset(
                clampToCampaign(lastDayOf(req.period), campaign)
            );
            const weeklyWindow = asPreset(
                clampToCampaign(lastWeekOf(req.period), campaign)
            );

            // Two separate rules: a selected period under a week has no
            // seven-day window to read, and a window clamped under a week is
            // days the campaign did not run, not a week to label as one.
            const weeklyEligible =
                daysInPeriod(req.period) >= WEEKLY_THRESHOLD_DAYS &&
                weeklyWindow != null &&
                daysInPeriod(weeklyWindow) >= WEEKLY_THRESHOLD_DAYS;

            // One wave: the weekly window depends on nothing the other two fetch,
            // so it goes out alongside them rather than after, on a page that
            // already paces requests behind the store's own concurrency gate.
            const [toDate, daily, weekly] = await Promise.all([
                windowRate(base, req, req.campaignToDate, deps),
                dailyWindow
                    ? windowRate(base, req, dailyWindow, deps)
                    : Promise.resolve(null),
                weeklyEligible && weeklyWindow
                    ? windowRate(base, req, weeklyWindow, deps)
                    : Promise.resolve(null),
            ]);
            if (!toDate.served) return toDate.state;

            // A genuinely unresolved fetch (not yet a failure) bubbles up as-is,
            // same discipline as the campaign-window trio: the card waits rather
            // than settling on a degraded read.
            if (daily && !daily.served && !hasFailed(daily.state)) {
                return daily.state;
            }
            if (weekly && !weekly.served && !hasFailed(weekly.state)) {
                return weekly.state;
            }

            // Deliberately not `withCampaignWindows`'s selected-window caveats:
            // rate-trio has no selected-period window, so the campaign window
            // (the one always served) carries them instead.
            const notes: EnvelopeCaveat[] = toDate.caveats
                ? [...toDate.caveats]
                : [];

            // Two independent fetches against two different windows fail
            // independently (the last day is the one most likely to come back
            // "not yet ingested"), so each degrades and is named on its own
            // rather than blanking the pair together.
            if (weekly !== null && !weekly.served && hasFailed(weekly.state)) {
                notes.push({
                    kind: 'warning',
                    code: 'weekly_window_unavailable',
                    text: `The last 7 days figure is unavailable: ${failureReason(
                        weekly.state
                    )}`,
                });
            }
            if (daily && !daily.served && hasFailed(daily.state)) {
                notes.push({
                    kind: 'warning',
                    code: 'daily_window_unavailable',
                    text: `The last day figure is unavailable: ${failureReason(
                        daily.state
                    )}`,
                });
            }

            return {
                status: 'ready',
                data: {
                    form: 'rate-trio',
                    toDate: toDate.window,
                    weekly:
                        weeklyEligible && weekly && weekly.served
                            ? weekly.window
                            : AVERAGE_UNAVAILABLE,
                    daily:
                        daily && daily.served
                            ? daily.window
                            : AVERAGE_UNAVAILABLE,
                    ...(req.metric != null &&
                        currencyMetrics.has(req.metric) && {
                            isCurrency: true,
                        }),
                    ...(req.metric != null &&
                        percentMetrics?.includes(req.metric) && {
                            isPercent: true,
                        }),
                },
                ...(notes.length > 0 && { caveats: notes }),
                ...oldestComputedAt(
                    toDate.computedAt,
                    daily?.served ? daily.computedAt : undefined,
                    weekly?.served ? weekly.computedAt : undefined
                ),
            };
        },
    };
};
